"use strict";
/**
 * Render tree node.
 * Copyright Metrological, 2017
 * Copyright Bas van Meurs, 2020
 */
Object.defineProperty(exports, "__esModule", { value: true });
const ElementCore_1 = require("./core/ElementCore");
const ElementChildList_1 = require("./ElementChildList");
const ElementListeners_1 = require("./ElementListeners");
const Utils_1 = require("./Utils");
class Element {
    constructor(stage) {
        this._id = undefined;
        this._ref = undefined;
        // An element is attached if it is a descendant of the stage root.
        this._attached = false;
        // An element is enabled when it is attached and it is visible (worldAlpha > 0).
        this._enabled = false;
        // An element is active when it is enabled and it is within bounds.
        this._active = false;
        this._parent = undefined;
        // The texture that is currently set.
        this._texture = undefined;
        // The currently displayed texture. May differ from this.texture while loading.
        this._displayedTexture = undefined;
        // Contains the child elements.
        this._childList = undefined;
        this.listeners = undefined;
        // Custom data
        this.data = undefined;
        this.stage = stage;
        this._core = new ElementCore_1.ElementCore(this);
    }
    getListeners() {
        if (!this.listeners) {
            this.listeners = new ElementListeners_1.ElementListeners();
        }
        return this.listeners;
    }
    get id() {
        return this._id;
    }
    set id(id) {
        const prevId = this._id;
        this._id = id;
        if (this._attached) {
            if (prevId) {
                this.stage.removeId(prevId, this);
            }
            if (id) {
                this.stage.addId(id, this);
            }
        }
    }
    set ref(ref) {
        if (this._ref !== ref) {
            if (this._ref !== undefined) {
                if (this._parent !== undefined) {
                    this._parent.childList.clearRef(this._ref);
                }
            }
            this._ref = ref;
            if (this._ref) {
                if (this._parent) {
                    this._parent.childList.setRef(this._ref, this);
                }
            }
        }
    }
    get ref() {
        return this._ref;
    }
    get core() {
        return this._core;
    }
    setAsRoot() {
        this._core.setupAsRoot();
        this._updateAttachedFlag();
        this._updateEnabledFlag();
    }
    get isRoot() {
        return this._core.isRoot;
    }
    _setParent(parent) {
        if (this._parent === parent)
            return;
        this._parent = parent;
        this._updateAttachedFlag();
        this._updateEnabledFlag();
        if (this.isRoot && parent) {
            this._throwError("Root should not be added as a child! Results are unspecified!");
        }
    }
    get attached() {
        return this._attached;
    }
    get enabled() {
        return this._enabled;
    }
    get active() {
        return this._active;
    }
    _isAttached() {
        return this._parent ? this._parent._attached : this.isRoot;
    }
    _isEnabled() {
        return this._core.visible && this._core.alpha > 0 && (this._parent ? this._parent._enabled : this.isRoot);
    }
    _isActive() {
        return this._isEnabled() && this.isWithinBoundsMargin();
    }
    _updateAttachedFlag() {
        const newAttached = this._isAttached();
        if (this._attached !== newAttached) {
            this._attached = newAttached;
            if (newAttached) {
                this._onSetup();
            }
            if (this._childList) {
                const children = this._childList.getItems();
                if (children) {
                    const m = children.length;
                    if (m > 0) {
                        for (let i = 0; i < m; i++) {
                            children[i]._updateAttachedFlag();
                        }
                    }
                }
            }
            if (newAttached) {
                this._onAttach();
            }
            else {
                this._onDetach();
            }
        }
    }
    _updateEnabledFlag() {
        const newEnabled = this._isEnabled();
        if (this._enabled !== newEnabled) {
            if (newEnabled) {
                this._onEnabled();
                this._setEnabledFlag();
            }
            else {
                this._onDisabled();
                this._unsetEnabledFlag();
            }
            if (this._childList) {
                const children = this._childList.getItems();
                if (children) {
                    const m = children.length;
                    if (m > 0) {
                        for (let i = 0; i < m; i++) {
                            children[i]._updateEnabledFlag();
                        }
                    }
                }
            }
        }
    }
    _setEnabledFlag() {
        this._enabled = true;
        // Force re-check of texture because dimensions might have changed (cutting).
        this._updateTextureDimensions();
        this.updateTextureCoords();
        if (this._texture) {
            this._texture.addElement(this);
        }
        if (this.isWithinBoundsMargin()) {
            this._setActiveFlag();
        }
        if (this._core.shader) {
            this._core.shader.addElement(this._core);
        }
    }
    _unsetEnabledFlag() {
        if (this._active) {
            this._unsetActiveFlag();
        }
        if (this._texture) {
            this._texture.removeElement(this);
        }
        if (this._core.shader) {
            this._core.shader.removeElement(this._core);
        }
        this._enabled = false;
    }
    _setActiveFlag() {
        this._active = true;
        // This must happen before enabling the texture, because it may already be loaded or load directly.
        if (this._texture) {
            this._texture.incActiveCount();
        }
        if (this._texture) {
            this._enableTexture();
        }
        this._onActive();
    }
    _unsetActiveFlag() {
        if (this._texture) {
            this._texture.decActiveCount();
        }
        this._active = false;
        if (this._texture) {
            this._disableTexture();
        }
        if (this._hasTexturizer()) {
            this.texturizer.deactivate();
        }
        this._onInactive();
    }
    set onSetup(v) {
        this.getListeners().onSetup = v;
    }
    get onSetup() {
        return this.getListeners().onSetup;
    }
    _onSetup() {
        if (this.listeners && this.listeners.onSetup) {
            this.listeners.onSetup({ element: this });
        }
    }
    set onAttach(v) {
        this.getListeners().onAttach = v;
    }
    get onAttach() {
        return this.getListeners().onAttach;
    }
    _onAttach() {
        if (this._id) {
            this.stage.addId(this._id, this);
        }
        if (this.listeners && this.listeners.onAttach) {
            this.listeners.onAttach({ element: this });
        }
    }
    set onDetach(v) {
        this.getListeners().onDetach = v;
    }
    get onDetach() {
        return this.getListeners().onDetach;
    }
    _onDetach() {
        if (this.listeners && this.listeners.onDetach) {
            this.listeners.onDetach({ element: this });
        }
        if (this._id) {
            this.stage.removeId(this._id, this);
        }
    }
    set onEnabled(v) {
        this.getListeners().onEnabled = v;
    }
    get onEnabled() {
        return this.getListeners().onEnabled;
    }
    _onEnabled() {
        if (this.listeners && this.listeners.onEnabled) {
            this.listeners.onEnabled({ element: this });
        }
    }
    set onDisabled(v) {
        this.getListeners().onDisabled = v;
    }
    get onDisabled() {
        return this.getListeners().onDisabled;
    }
    _onDisabled() {
        if (this.listeners && this.listeners.onDisabled) {
            this.listeners.onDisabled({ element: this });
        }
    }
    set onActive(v) {
        this.getListeners().onActive = v;
    }
    get onActive() {
        return this.getListeners().onActive;
    }
    _onActive() {
        if (this.listeners && this.listeners.onActive) {
            this.listeners.onActive({ element: this });
        }
    }
    set onInactive(v) {
        this.getListeners().onInactive = v;
    }
    get onInactive() {
        return this.getListeners().onInactive;
    }
    _onInactive() {
        if (this.listeners && this.listeners.onInactive) {
            this.listeners.onInactive({ element: this });
        }
    }
    set onTextureError(v) {
        this.getListeners().onTextureError = v;
    }
    get onTextureError() {
        return this.getListeners().onTextureError;
    }
    _onTextureError(loadError, texture) {
        if (this.listeners && this.listeners.onTextureError) {
            this.listeners.onTextureError({ element: this, texture, error: loadError });
        }
    }
    set onTextureLoaded(v) {
        this.getListeners().onTextureLoaded = v;
    }
    get onTextureLoaded() {
        return this.getListeners().onTextureLoaded;
    }
    _onTextureLoaded(texture) {
        if (this.listeners && this.listeners.onTextureLoaded) {
            this.listeners.onTextureLoaded({ element: this, texture });
        }
    }
    set onTextureUnloaded(v) {
        this.getListeners().onTextureUnloaded = v;
    }
    get onTextureUnloaded() {
        return this.getListeners().onTextureUnloaded;
    }
    _onTextureUnloaded(texture) {
        if (this.listeners && this.listeners.onTextureUnloaded) {
            this.listeners.onTextureUnloaded({ element: this, texture });
        }
    }
    set onResize(v) {
        this.getListeners().onResize = v;
    }
    get onResize() {
        return this.getListeners().onResize;
    }
    _onResize(w, h) {
        if (this.listeners && this.listeners.onResize) {
            this.listeners.onResize({ element: this, w, h });
        }
    }
    get renderWidth() {
        return this._core.getRenderWidth();
    }
    get renderHeight() {
        return this._core.getRenderHeight();
    }
    get layoutX() {
        return this._core.getLayoutX();
    }
    get layoutY() {
        return this._core.getLayoutY();
    }
    get layoutW() {
        return this._core.getLayoutW();
    }
    get layoutH() {
        return this._core.getLayoutH();
    }
    textureIsLoaded() {
        return this._texture ? this._texture.isLoaded() : false;
    }
    loadTexture() {
        if (this._texture) {
            this._texture.load();
            if (!this._texture.isUsed() || !this._isEnabled()) {
                // As this element is invisible, loading the texture will have no effect on the dimensions of this element.
                // To help the developers, automatically update the dimensions.
                this._updateTextureDimensions();
            }
        }
    }
    _enableTextureError() {
        // txError event should automatically be re-triggered when a element becomes active.
        const loadError = this._texture.loadError;
        if (loadError) {
            this._onTextureError(loadError, this._texture);
        }
    }
    _enableTexture() {
        if (this._texture.isLoaded()) {
            this.setDisplayedTexture(this._texture);
        }
        else {
            // We don't want to retain the old image as it wasn't visible anyway.
            this.setDisplayedTexture(undefined);
            this._enableTextureError();
        }
    }
    _disableTexture() {
        // We disable the displayed texture because, when the texture changes while invisible, we should use that w, h,
        // mw, mh for checking within bounds.
        this.setDisplayedTexture(undefined);
    }
    get texture() {
        return this._texture;
    }
    set texture(v) {
        let texture;
        if (!v) {
            texture = undefined;
        }
        else {
            texture = v;
        }
        const prevTexture = this._texture;
        if (texture !== prevTexture) {
            this._texture = texture;
            if (this._texture) {
                if (this._enabled) {
                    this._texture.addElement(this);
                    if (this.isWithinBoundsMargin()) {
                        if (this._texture.isLoaded()) {
                            this.setDisplayedTexture(this._texture);
                        }
                        else {
                            this._enableTextureError();
                        }
                    }
                }
            }
            else {
                // Make sure that current texture is cleared when the texture is explicitly set to undefined.
                this.setDisplayedTexture(undefined);
            }
            if (prevTexture && prevTexture !== this._displayedTexture) {
                prevTexture.removeElement(this);
            }
            this._updateTextureDimensions();
        }
    }
    get displayedTexture() {
        return this._displayedTexture;
    }
    setDisplayedTexture(v) {
        const prevTexture = this._displayedTexture;
        if (prevTexture && v !== prevTexture) {
            if (this._texture !== prevTexture) {
                // The old displayed texture is deprecated.
                prevTexture.removeElement(this);
            }
        }
        const prevSource = this._core.displayedTextureSource ? this._core.displayedTextureSource : undefined;
        const sourceChanged = (v ? v.getSource() : undefined) !== prevSource;
        this._displayedTexture = v;
        this._updateTextureDimensions();
        if (this._displayedTexture) {
            if (sourceChanged) {
                // We don't need to reference the displayed texture because it was already referenced (this.texture === this.displayedTexture).
                this.updateTextureCoords();
                this._core.setDisplayedTextureSource(this._displayedTexture.getSource());
            }
        }
        else {
            this._core.setDisplayedTextureSource(undefined);
        }
        if (sourceChanged) {
            if (this._displayedTexture) {
                this._onTextureLoaded(this._displayedTexture);
            }
            else if (prevTexture) {
                this._onTextureUnloaded(prevTexture);
            }
        }
    }
    onTextureSourceLoaded() {
        // This function is called when element is enabled, but we only want to set displayed texture for active elements.
        if (this.active) {
            // We may be dealing with a texture reloading, so we must force update.
            this.setDisplayedTexture(this._texture);
        }
    }
    onTextureSourceLoadError(loadError) {
        this._onTextureError(loadError, this._texture);
    }
    forceRenderUpdate() {
        this._core.setHasRenderUpdates(3);
    }
    onDisplayedTextureClippingChanged() {
        this._updateTextureDimensions();
        this.updateTextureCoords();
    }
    onPixelRatioChanged() {
        this._updateTextureDimensions();
    }
    _updateTextureDimensions() {
        let w = 0;
        let h = 0;
        if (this._displayedTexture) {
            w = this._displayedTexture.getRenderWidth();
            h = this._displayedTexture.getRenderHeight();
        }
        else if (this._texture) {
            // Texture already loaded, but not yet updated (probably because this element is not active).
            w = this._texture.getRenderWidth();
            h = this._texture.getRenderHeight();
        }
        let unknownSize = false;
        if (!w || !h) {
            if (!this._displayedTexture && this._texture) {
                // We use a 'max width' replacement instead in the ElementCore calcs.
                // This makes sure that it is able to determine withinBounds.
                w = w || this._texture.mw;
                h = h || this._texture.mh;
                if ((!w || !h) && this._texture.isAutosizeTexture()) {
                    unknownSize = true;
                }
            }
        }
        this._core.setTextureDimensions(w, h, unknownSize);
    }
    updateTextureCoords() {
        if (this.displayedTexture) {
            const displayedTexture = this.displayedTexture;
            const displayedTextureSource = this.displayedTexture.getSource();
            if (displayedTextureSource) {
                let tx1 = 0;
                let ty1 = 0;
                let tx2 = 1.0;
                let ty2 = 1.0;
                if (displayedTexture.hasClipping()) {
                    // Apply texture clipping.
                    const w = displayedTextureSource.getRenderWidth();
                    const h = displayedTextureSource.getRenderHeight();
                    let iw;
                    let ih;
                    let rw;
                    let rh;
                    iw = 1 / w;
                    ih = 1 / h;
                    if (displayedTexture.pw) {
                        rw = displayedTexture.pw * iw;
                    }
                    else {
                        rw = (w - displayedTexture.px) * iw;
                    }
                    if (displayedTexture.ph) {
                        rh = displayedTexture.ph * ih;
                    }
                    else {
                        rh = (h - displayedTexture.py) * ih;
                    }
                    iw *= displayedTexture.px;
                    ih *= displayedTexture.py;
                    tx1 = iw;
                    ty1 = ih;
                    tx2 = tx2 * rw + iw;
                    ty2 = ty2 * rh + ih;
                    tx1 = Math.max(0, tx1);
                    ty1 = Math.max(0, ty1);
                    tx2 = Math.min(1, tx2);
                    ty2 = Math.min(1, ty2);
                }
                this._core.setTextureCoords(tx1, ty1, tx2, ty2);
            }
        }
    }
    getCornerPoints() {
        return this._core.getCornerPoints();
    }
    getByRef(ref) {
        return this.childList.getByRef(ref);
    }
    getLocationString() {
        const i = this._parent ? this._parent.childList.getIndex(this) : "R";
        let str = this._parent ? this._parent.getLocationString() : "";
        if (this.ref) {
            str += ":[" + i + "]" + this.ref;
        }
        else {
            str += ":[" + i + "]#" + this.id;
        }
        return str;
    }
    toString() {
        const obj = this.getSettings();
        return Element.getPrettyString(obj, "");
    }
    static getPrettyString(obj, indent) {
        const children = obj.children;
        delete obj.children;
        // Convert singular json settings object.
        const colorKeys = ["color", "colorUl", "colorUr", "colorBl", "colorBr"];
        let str = JSON.stringify(obj, (k, v) => {
            if (colorKeys.indexOf(k) !== -1) {
                return "COLOR[" + v.toString(16) + "]";
            }
            return v;
        });
        str = str.replace(/"COLOR\[([a-f0-9]{1,8})\]"/g, "0x$1");
        if (children) {
            let childStr = "";
            if (Utils_1.Utils.isObjectLiteral(children)) {
                const refs = Object.keys(children);
                childStr = "";
                for (let i = 0, n = refs.length; i < n; i++) {
                    childStr += `\n${indent}  "${refs[i]}":`;
                    delete children[refs[i]].ref;
                    childStr += Element.getPrettyString(children[refs[i]], indent + "  ") + (i < n - 1 ? "," : "");
                }
                const isEmpty = str === "{}";
                str = str.substr(0, str.length - 1) + (isEmpty ? "" : ",") + childStr + "\n" + indent + "}";
            }
            else {
                const n = children.length;
                childStr = "[";
                for (let i = 0; i < n; i++) {
                    childStr += Element.getPrettyString(children[i], indent + "  ") + (i < n - 1 ? "," : "") + "\n";
                }
                childStr += indent + "]}";
                const isEmpty = str === "{}";
                str = str.substr(0, str.length - 1) + (isEmpty ? "" : ",") + '"children":\n' + indent + childStr + "}";
            }
        }
        return str;
    }
    getSettings() {
        const settings = this.getNonDefaults();
        const children = this.childList.getItems();
        if (children) {
            const n = children.length;
            if (n) {
                const childArray = [];
                let missing = false;
                for (let i = 0; i < n; i++) {
                    childArray.push(children[i].getSettings());
                    missing = missing || !children[i].ref;
                }
                if (!missing) {
                    settings.children = {};
                    childArray.forEach((child) => {
                        settings.children[child.ref] = child;
                    });
                }
                else {
                    settings.children = childArray;
                }
            }
        }
        settings.id = this.id;
        return settings;
    }
    getNonDefaults() {
        const settings = {};
        if (this.constructor !== Element) {
            settings.type = this.constructor.name;
        }
        if (this._ref) {
            settings.ref = this._ref;
        }
        if (this.x !== 0)
            settings.x = this.x;
        if (this.y !== 0)
            settings.y = this.y;
        if (this.w !== 0)
            settings.w = this.w;
        if (this.h !== 0)
            settings.h = this.h;
        if (this.scaleX === this.scaleY) {
            if (this.scaleX !== 1)
                settings.scale = this.scaleX;
        }
        else {
            if (this.scaleX !== 1)
                settings.scaleX = this.scaleX;
            if (this.scaleY !== 1)
                settings.scaleY = this.scaleY;
        }
        if (this.pivotX === this.pivotY) {
            if (this.pivotX !== 0.5)
                settings.pivot = this.pivotX;
        }
        else {
            if (this.pivotX !== 0.5)
                settings.pivotX = this.pivotX;
            if (this.pivotY !== 0.5)
                settings.pivotY = this.pivotY;
        }
        if (this.mountX === this.mountY) {
            if (this.mountX !== 0)
                settings.mount = this.mountX;
        }
        else {
            if (this.mountX !== 0)
                settings.mountX = this.mountX;
            if (this.mountY !== 0)
                settings.mountY = this.mountY;
        }
        if (this.alpha !== 1)
            settings.alpha = this.alpha;
        if (!this.visible)
            settings.visible = false;
        if (this.rotation !== 0)
            settings.rotation = this.rotation;
        if (this.colorUl === this.colorUr && this.colorBl === this.colorBr && this.colorUl === this.colorBl) {
            if (this.colorUl !== 0xffffffff)
                settings.color = this.colorUl.toString(16);
        }
        else {
            if (this.colorUl !== 0xffffffff)
                settings.colorUl = this.colorUl.toString(16);
            if (this.colorUr !== 0xffffffff)
                settings.colorUr = this.colorUr.toString(16);
            if (this.colorBl !== 0xffffffff)
                settings.colorBl = this.colorBl.toString(16);
            if (this.colorBr !== 0xffffffff)
                settings.colorBr = this.colorBr.toString(16);
        }
        if (this.zIndex)
            settings.zIndex = this.zIndex;
        if (this.forceZIndexContext)
            settings.forceZIndexContext = true;
        if (this.clipping)
            settings.clipping = this.clipping;
        if (!this.clipbox)
            settings.clipbox = this.clipbox;
        if (this._texture) {
            const tnd = this._texture.getNonDefaults();
            if (Object.keys(tnd).length) {
                settings.texture = tnd;
            }
        }
        if (this._hasTexturizer()) {
            if (this.texturizer.enabled) {
                settings.renderToTexture = this.texturizer.enabled;
            }
            if (this.texturizer.lazy) {
                settings.renderToTextureLazy = true;
            }
            if (this.texturizer.colorize) {
                settings.colorizeResultTexture = this.texturizer.colorize;
            }
            if (this.texturizer.renderOffscreen) {
                settings.renderOffscreen = this.texturizer.renderOffscreen;
            }
        }
        return settings;
    }
    isWithinBoundsMargin() {
        return this._core.isWithinBoundsMargin();
    }
    _enableWithinBoundsMargin() {
        // Iff enabled, this toggles the active flag.
        if (this._enabled) {
            this._setActiveFlag();
        }
    }
    _disableWithinBoundsMargin() {
        // Iff active, this toggles the active flag.
        if (this._active) {
            this._unsetActiveFlag();
        }
    }
    set boundsMargin(v) {
        if (v === undefined) {
            this._core.boundsMargin = undefined;
        }
        else {
            this._core.boundsMargin = [v, v, v, v];
        }
    }
    get boundsMargin() {
        return this._core.boundsMargin ? this._core.boundsMargin[0] : undefined;
    }
    set boundsMarginLeft(v) {
        if (!this._core.boundsMargin) {
            this._core.boundsMargin = [v, 100, 100, 100];
        }
        else {
            this._core.boundsMargin[0] = v;
        }
    }
    get boundsMarginLeft() {
        return this._core.boundsMargin ? this._core.boundsMargin[0] : 100;
    }
    set boundsMarginTop(v) {
        if (!this._core.boundsMargin) {
            this._core.boundsMargin = [100, v, 100, 100];
        }
        else {
            this._core.boundsMargin[1] = v;
        }
    }
    get boundsMarginTop() {
        return this._core.boundsMargin ? this._core.boundsMargin[1] : 100;
    }
    set boundsMarginRight(v) {
        if (!this._core.boundsMargin) {
            this._core.boundsMargin = [100, 100, v, 100];
        }
        else {
            this._core.boundsMargin[2] = v;
        }
    }
    get boundsMarginRight() {
        return this._core.boundsMargin ? this._core.boundsMargin[2] : 100;
    }
    set boundsMarginBottom(v) {
        if (!this._core.boundsMargin) {
            this._core.boundsMargin = [100, 100, 100, v];
        }
        else {
            this._core.boundsMargin[3] = v;
        }
    }
    get boundsMarginBottom() {
        return this._core.boundsMargin ? this._core.boundsMargin[3] : 100;
    }
    get x() {
        return this._core.x;
    }
    set x(v) {
        this._core.x = v;
    }
    get funcX() {
        return this._core.funcX;
    }
    set funcX(v) {
        this._core.funcX = v;
    }
    get y() {
        return this._core.y;
    }
    set y(v) {
        this._core.y = v;
    }
    get funcY() {
        return this._core.funcY;
    }
    set funcY(v) {
        this._core.funcY = v;
    }
    get w() {
        return this._core.w;
    }
    set w(v) {
        this._core.w = v;
    }
    get funcW() {
        return this._core.funcW;
    }
    set funcW(v) {
        this._core.funcW = v;
    }
    get h() {
        return this._core.h;
    }
    set h(v) {
        this._core.h = v;
    }
    get funcH() {
        return this._core.funcH;
    }
    set funcH(v) {
        this._core.funcH = v;
    }
    get scaleX() {
        return this._core.scaleX;
    }
    set scaleX(v) {
        this._core.scaleX = v;
    }
    get scaleY() {
        return this._core.scaleY;
    }
    set scaleY(v) {
        this._core.scaleY = v;
    }
    get scale() {
        return this._core.scale;
    }
    set scale(v) {
        this._core.scale = v;
    }
    get pivotX() {
        return this._core.pivotX;
    }
    set pivotX(v) {
        this._core.pivotX = v;
    }
    get pivotY() {
        return this._core.pivotY;
    }
    set pivotY(v) {
        this._core.pivotY = v;
    }
    get pivot() {
        return this._core.pivot;
    }
    set pivot(v) {
        this._core.pivot = v;
    }
    get mountX() {
        return this._core.mountX;
    }
    set mountX(v) {
        this._core.mountX = v;
    }
    get mountY() {
        return this._core.mountY;
    }
    set mountY(v) {
        this._core.mountY = v;
    }
    get mount() {
        return this._core.mount;
    }
    set mount(v) {
        this._core.mount = v;
    }
    get rotation() {
        return this._core.rotation;
    }
    set rotation(v) {
        this._core.rotation = v;
    }
    get alpha() {
        return this._core.alpha;
    }
    set alpha(v) {
        this._core.alpha = v;
    }
    get visible() {
        return this._core.visible;
    }
    set visible(v) {
        this._core.visible = v;
    }
    get colorUl() {
        return this._core.colorUl;
    }
    set colorUl(v) {
        this._core.colorUl = v;
    }
    get colorUr() {
        return this._core.colorUr;
    }
    set colorUr(v) {
        this._core.colorUr = v;
    }
    get colorBl() {
        return this._core.colorBl;
    }
    set colorBl(v) {
        this._core.colorBl = v;
    }
    get colorBr() {
        return this._core.colorBr;
    }
    set colorBr(v) {
        this._core.colorBr = v;
    }
    get color() {
        return this._core.colorUl;
    }
    set color(v) {
        if (this.colorUl !== v || this.colorUr !== v || this.colorBl !== v || this.colorBr !== v) {
            this.colorUl = v;
            this.colorUr = v;
            this.colorBl = v;
            this.colorBr = v;
        }
    }
    get colorTop() {
        return this.colorUl;
    }
    set colorTop(v) {
        if (this.colorUl !== v || this.colorUr !== v) {
            this.colorUl = v;
            this.colorUr = v;
        }
    }
    get colorBottom() {
        return this.colorBl;
    }
    set colorBottom(v) {
        if (this.colorBl !== v || this.colorBr !== v) {
            this.colorBl = v;
            this.colorBr = v;
        }
    }
    get colorLeft() {
        return this.colorUl;
    }
    set colorLeft(v) {
        if (this.colorUl !== v || this.colorBl !== v) {
            this.colorUl = v;
            this.colorBl = v;
        }
    }
    get colorRight() {
        return this.colorUr;
    }
    set colorRight(v) {
        if (this.colorUr !== v || this.colorBr !== v) {
            this.colorUr = v;
            this.colorBr = v;
        }
    }
    get zIndex() {
        return this._core.zIndex;
    }
    set zIndex(v) {
        this._core.zIndex = v;
    }
    get forceZIndexContext() {
        return this._core.forceZIndexContext;
    }
    set forceZIndexContext(v) {
        this._core.forceZIndexContext = v;
    }
    get clipping() {
        return this._core.clipping;
    }
    set clipping(v) {
        this._core.clipping = v;
    }
    get clipbox() {
        return this._core.clipbox;
    }
    set clipbox(v) {
        this._core.clipbox = v;
    }
    get skipInLayout() {
        return this._core.skipInLayout;
    }
    set skipInLayout(v) {
        this._core.skipInLayout = v;
    }
    get childList() {
        if (!this._childList) {
            this._childList = new ElementChildList_1.ElementChildList(this);
        }
        return this._childList;
    }
    hasChildren() {
        return this._childList && this._childList.length > 0;
    }
    get children() {
        return this.childList.getItems();
    }
    set children(items) {
        this.childList.setItems(items);
    }
    get p() {
        return this._parent;
    }
    get parent() {
        return this._parent;
    }
    set mw(v) {
        if (this.texture) {
            this.texture.mw = v;
            this._updateTextureDimensions();
        }
        else {
            this._throwError("Set mw after setting a texture.");
        }
    }
    get mw() {
        return this.texture ? this.texture.mw : 0;
    }
    set mh(v) {
        if (this.texture) {
            this.texture.mh = v;
            this._updateTextureDimensions();
        }
        else {
            this._throwError("Set mh after setting a texture.");
        }
    }
    get mh() {
        return this.texture ? this.texture.mh : 0;
    }
    set onUpdate(f) {
        this._core.onUpdate = f;
    }
    get onUpdate() {
        return this._core.onUpdate;
    }
    set onAfterCalcs(f) {
        this._core.onAfterCalcs = f;
    }
    get onAfterCalcs() {
        return this._core.onAfterCalcs;
    }
    set onAfterUpdate(f) {
        this._core.onAfterUpdate = f;
    }
    get onAfterUpdate() {
        return this._core.onAfterUpdate;
    }
    forceUpdate() {
        // Make sure that the update loop is run.
        this._core._setHasUpdates();
    }
    get shader() {
        return this._core.shader;
    }
    set shader(shader) {
        if (this._enabled && this._core.shader) {
            this._core.shader.removeElement(this._core);
        }
        this._core.shader = shader;
        if (this._enabled && this._core.shader) {
            this._core.shader.addElement(this._core);
        }
    }
    _hasTexturizer() {
        return this._core.hasTexturizer();
    }
    get renderToTexture() {
        return this._hasTexturizer() && this.texturizer.enabled;
    }
    set renderToTexture(v) {
        this.texturizer.enabled = v;
    }
    get renderToTextureLazy() {
        return this._hasTexturizer() && this.texturizer.lazy;
    }
    set renderToTextureLazy(v) {
        this.texturizer.lazy = v;
    }
    get renderToTextureOffscreen() {
        return this._hasTexturizer() && this.texturizer.renderOffscreen;
    }
    set renderToTextureOffscreen(v) {
        this.texturizer.renderOffscreen = v;
    }
    get renderToTextureColorize() {
        return this._hasTexturizer() && this.texturizer.colorize;
    }
    set renderToTextureColorize(v) {
        this.texturizer.colorize = v;
    }
    getTexture() {
        return this.texturizer._getTextureSource();
    }
    get texturizer() {
        return this._core.texturizer;
    }
    _throwError(message) {
        throw new Error(this.constructor.name + " (" + this.getLocationString() + "): " + message);
    }
    get _flex() {
        return this._core.layout.flex;
    }
    get _flexItem() {
        return this._core.layout.flexItem;
    }
    set flex(v) {
        this._flex.enabled = v;
    }
    get flex() {
        return this._flex.enabled;
    }
    set flexDirection(v) {
        this._flex.direction = v;
    }
    get flexDirection() {
        return this._flex.direction;
    }
    set flexWrap(v) {
        this._flex.wrap = v;
    }
    get flexWrap() {
        return this._flex.wrap;
    }
    set flexAlignItems(v) {
        this._flex.alignItems = v;
    }
    get flexAlignItems() {
        return this._flex.alignItems;
    }
    set flexJustifyContent(v) {
        this._flex.justifyContent = v;
    }
    get flexJustifyContent() {
        return this._flex.justifyContent;
    }
    set flexAlignContent(v) {
        this._flex.alignContent = v;
    }
    get flexAlignContent() {
        return this._flex.alignContent;
    }
    set flexItem(v) {
        this._flexItem.enabled = v;
    }
    get flexItem() {
        return this._flexItem.enabled;
    }
    set flexGrow(v) {
        this._flexItem.grow = v;
    }
    get flexGrow() {
        return this._flexItem.grow;
    }
    set flexShrink(v) {
        this._flexItem.shrink = v;
    }
    get flexShrink() {
        return this._flexItem.shrink;
    }
    set flexAlignSelf(v) {
        this._flexItem.alignSelf = v;
    }
    get flexAlignSelf() {
        return this._flexItem.alignSelf;
    }
    set padding(v) {
        this._flex.padding = v;
    }
    get padding() {
        return this._flex.padding;
    }
    set paddingLeft(v) {
        this._flex.paddingLeft = v;
    }
    get paddingLeft() {
        return this._flex.paddingLeft;
    }
    set paddingRight(v) {
        this._flex.paddingRight = v;
    }
    get paddingRight() {
        return this._flex.paddingRight;
    }
    set paddingTop(v) {
        this._flex.paddingTop = v;
    }
    get paddingTop() {
        return this._flex.paddingTop;
    }
    set paddingBottom(v) {
        this._flex.paddingBottom = v;
    }
    get paddingBottom() {
        return this._flex.paddingBottom;
    }
    set margin(v) {
        this._flexItem.margin = v;
    }
    get margin() {
        return this._flexItem.margin;
    }
    set marginLeft(v) {
        this._flexItem.marginLeft = v;
    }
    get marginLeft() {
        return this._flexItem.marginLeft;
    }
    set marginRight(v) {
        this._flexItem.marginRight = v;
    }
    get marginRight() {
        return this._flexItem.marginRight;
    }
    set marginTop(v) {
        this._flexItem.marginTop = v;
    }
    get marginTop() {
        return this._flexItem.marginTop;
    }
    set marginBottom(v) {
        this._flexItem.marginBottom = v;
    }
    get marginBottom() {
        return this._flexItem.marginBottom;
    }
    set minWidth(v) {
        this._flexItem.minWidth = v;
    }
    get minWidth() {
        return this._flexItem.minWidth;
    }
    set maxWidth(v) {
        this._flexItem.maxWidth = v;
    }
    get maxWidth() {
        return this._flexItem.maxWidth;
    }
    set minHeight(v) {
        this._flexItem.minHeight = v;
    }
    get minHeight() {
        return this._flexItem.minHeight;
    }
    set maxHeight(v) {
        this._flexItem.maxHeight = v;
    }
    get maxHeight() {
        return this._flexItem.maxHeight;
    }
}
exports.Element = Element;
//# sourceMappingURL=Element.js.map